import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PLOT_SPACING } from '../game/constants';
import type { GameState } from '../game/state';
import { GeometryBuilder, mul, Templates as T, trs } from './GeometryBuilder';
import { LAYOUT } from './layout';
import { toon, toonUnique } from './materials';
import { SignTexture } from './textures';

export type PropId = 'market' | 'board' | 'house';

export interface PropPick {
  kind: 'prop';
  prop: PropId;
}

const WOOD = '#c98b55';
const WOOD_DARK = '#9c6b45';
const WOOD_LIGHT = '#e7b47a';

const vertexToon = toonUnique({ vertexColors: true });

function staticMesh(build: (b: GeometryBuilder) => void): THREE.Mesh {
  const b = new GeometryBuilder();
  build(b);
  const mesh = new THREE.Mesh(b.build().base, vertexToon);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** Invisible box used for raycast picking of a prop. */
function pickBox(width: number, height: number, depth: number, prop: PropId): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(width, height, depth),
    new THREE.MeshBasicMaterial(),
  );
  mesh.visible = false;
  mesh.position.y = height / 2;
  mesh.userData.pick = { kind: 'prop', prop } satisfies PropPick;
  return mesh;
}

function signMesh(sign: SignTexture, width: number, height: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({ map: sign.texture, transparent: true }),
  );
  return mesh;
}

function buildHouse(windowMaterial: THREE.Material): THREE.Group {
  const group = new THREE.Group();
  const pitch = 0.6;
  const wallTop = 3.5;
  const ridge = wallTop + 4 * Math.tan(pitch);
  group.add(
    staticMesh((b) => {
      b.add(T.box, '#b8aa98', trs([0, 0.25, 0], [0, 0, 0], [8.6, 0.5, 6.6]));
      b.add(T.box, '#fff0d4', trs([0, 2, 0], [0, 0, 0], [8, 3, 6]), { color2: '#fff8ea' });
      for (const [x, z] of [
        [-4, 3],
        [4, 3],
        [-4, -3],
        [4, -3],
      ] as const) {
        b.add(T.box, WOOD, trs([x, 2, z], [0, 0, 0], [0.32, 3, 0.32]));
      }
      // Gable ends fill the space under the roof.
      const gable = new THREE.Shape([
        new THREE.Vector2(-4, 0),
        new THREE.Vector2(4, 0),
        new THREE.Vector2(0, ridge - wallTop),
      ]);
      const gableGeometry = new THREE.ExtrudeGeometry(gable, { depth: 6, bevelEnabled: false });
      b.add(gableGeometry, '#fff3dc', trs([0, wallTop, -3]));
      gableGeometry.dispose();
      // Roof slabs.
      const slabLength = 4.6 / Math.cos(pitch);
      for (const side of [-1, 1]) {
        b.add(
          T.box,
          '#ef7b7b',
          trs(
            [side * 2.3, (ridge + ridge - 4.6 * Math.tan(pitch)) / 2 + 0.18, 0],
            [0, 0, -side * pitch],
            [slabLength, 0.36, 7],
          ),
          { color2: '#ff9f9f', axis: 'x' },
        );
      }
      b.add(T.box, '#d95f5f', trs([0, ridge + 0.32, 0], [0, 0, Math.PI / 4], [0.45, 0.45, 7.1]));
      // Chimney.
      b.add(T.box, '#c97b63', trs([2.2, 5.4, -1.2], [0, 0, 0], [0.8, 2, 0.8]));
      b.add(T.box, '#9d5d4a', trs([2.2, 6.45, -1.2], [0, 0, 0], [1, 0.2, 1]));
      // Door, frame and step.
      b.add(T.box, '#7a4a2a', trs([0, 1.6, 3.02], [0, 0, 0], [1.6, 2.3, 0.12]));
      b.add(T.box, '#a8683e', trs([0, 1.55, 3.08], [0, 0, 0], [1.3, 2.1, 0.08]), {
        color2: '#c07a4a',
      });
      b.add(T.sphere, '#ffd166', trs([0.45, 1.5, 3.15], [0, 0, 0], 0.08));
      b.add(T.box, '#cfc4b2', trs([0, 0.12, 3.7], [0, 0, 0], [2.4, 0.25, 1.1]));
      // Window frames and flower boxes.
      for (const x of [-2.6, 2.6]) {
        b.add(T.box, '#ffffff', trs([x, 2.25, 3.02], [0, 0, 0], [1.4, 1.3, 0.1]));
        b.add(T.box, WOOD_DARK, trs([x, 1.45, 3.2], [0, 0, 0], [1.5, 0.32, 0.4]));
        for (let i = 0; i < 5; i++) {
          const color = ['#ff8fab', '#ffd166', '#b79cff', '#ff8fab', '#ffffff'][i] ?? '#fff';
          b.add(T.sphereLow, color, trs([x - 0.56 + i * 0.28, 1.68, 3.2], [0, 0, 0], 0.13));
        }
      }
      // Lantern bracket.
      b.add(T.box, '#4a4a4a', trs([1.15, 2.7, 3.15], [0, 0, 0], [0.22, 0.32, 0.22]));
    }),
  );
  // Window panes glow at night.
  for (const x of [-2.6, 2.6]) {
    const pane = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 1.0), windowMaterial);
    pane.position.set(x, 2.25, 3.08);
    group.add(pane);
    const crossH = new THREE.Mesh(T.box, toon('#ffffff'));
    crossH.scale.set(1.1, 0.08, 0.04);
    crossH.position.set(x, 2.25, 3.1);
    const crossV = crossH.clone();
    crossV.scale.set(0.08, 1.0, 0.04);
    group.add(crossH, crossV);
  }
  const lantern = new THREE.Mesh(T.sphere, windowMaterial);
  lantern.scale.setScalar(0.13);
  lantern.position.set(1.15, 2.5, 3.2);
  group.add(lantern);
  group.add(pickBox(8.6, 7, 6.8, 'house'));
  return group;
}

function buildWindmill(): { group: THREE.Group; blades: THREE.Group } {
  const group = new THREE.Group();
  const tower = new THREE.CylinderGeometry(1.0, 1.6, 7, 8, 1).translate(0, 3.5, 0);
  group.add(
    staticMesh((b) => {
      b.add(tower, '#fff3dc', new THREE.Matrix4(), { color2: '#fffaf0' });
      b.add(new THREE.CylinderGeometry(1.38, 1.42, 0.3, 8), WOOD, trs([0, 2.2, 0]));
      b.add(new THREE.CylinderGeometry(1.16, 1.2, 0.3, 8), WOOD, trs([0, 4.8, 0]));
      b.add(T.cone, '#ef7b7b', trs([0, 7, 0], [0, 0, 0], [1.6, 2, 1.6]), { color2: '#ff9f9f' });
      b.add(T.box, '#7a4a2a', trs([0, 0.9, 1.5], [0.12, 0, 0], [0.9, 1.6, 0.1]));
      b.add(T.box, '#fff', trs([0, 4, 1.18], [0.08, 0, 0], [0.6, 0.6, 0.1]));
      b.add(T.sphere, '#9c6b45', trs([0, 6.6, 1.25], [0, 0, 0], 0.35));
    }),
  );
  tower.dispose();

  const blades = new THREE.Group();
  blades.position.set(0, 6.6, 1.5);
  const blade = staticMesh((b) => {
    for (let i = 0; i < 4; i++) {
      const rot = trs([0, 0, 0], [0, 0, (i * Math.PI) / 2]);
      b.add(T.box, WOOD_DARK, mul(rot, trs([0, 1.9, 0], [0, 0, 0], [0.16, 3.8, 0.12])));
      b.add(T.box, '#fffaf0', mul(rot, trs([0.32, 2.3, 0.02], [0, 0, 0], [0.55, 2.9, 0.05])), {
        color2: '#ffe3ec',
      });
    }
  });
  blades.add(blade);
  group.add(blades);

  // Hay bales nearby.
  group.add(
    staticMesh((b) => {
      const bale = new THREE.CylinderGeometry(0.6, 0.6, 1.1, 12);
      b.add(bale, '#f2cf63', trs([2.6, 0.6, 2.4], [0, 0, Math.PI / 2]), { color2: '#e2b84a' });
      b.add(bale, '#f2cf63', trs([3.4, 0.6, 1.1], [0, 0.5, Math.PI / 2]), { color2: '#e2b84a' });
      b.add(bale, '#f2cf63', trs([3.0, 1.6, 1.8], [0, 0.25, Math.PI / 2]), { color2: '#e2b84a' });
      bale.dispose();
    }),
  );
  return { group, blades };
}

function buildMarket(sign: SignTexture): THREE.Group {
  const group = new THREE.Group();
  group.add(
    staticMesh((b) => {
      b.add(T.box, WOOD, trs([0, 0.5, 0], [0, 0, 0], [3.2, 1, 1.2]), { color2: WOOD_LIGHT });
      b.add(T.box, WOOD_LIGHT, trs([0, 1.06, 0], [0, 0, 0], [3.4, 0.12, 1.4]));
      for (const [x, z] of [
        [-1.55, 0.6],
        [1.55, 0.6],
        [-1.55, -0.6],
        [1.55, -0.6],
      ] as const) {
        b.add(T.cylinder, WOOD_DARK, trs([x, 0, z], [0, 0, 0], [0.08, z > 0 ? 2.5 : 2.9, 0.08]));
      }
      // Striped awning with a scalloped edge.
      for (let i = 0; i < 6; i++) {
        const color = i % 2 === 0 ? '#ff8fab' : '#fff5f8';
        b.add(
          T.box,
          color,
          trs([-1.5 + i * 0.6 + 0.3, 2.75, 0.05], [0.32, 0, 0], [0.6, 0.08, 1.9]),
        );
        b.add(
          T.sphereLow,
          color,
          trs([-1.5 + i * 0.6 + 0.3, 2.42, 0.98], [0, 0, 0], [0.3, 0.16, 0.08]),
        );
      }
      // Crates of flowers on the counter.
      const bunches = ['#ff8fab', '#ffd166', '#b79cff'];
      bunches.forEach((color, i) => {
        const x = -1 + i;
        b.add(T.box, '#b07a48', trs([x, 1.3, 0], [0, 0.1 * i, 0], [0.7, 0.36, 0.55]));
        for (let k = 0; k < 6; k++) {
          b.add(
            T.sphereLow,
            color,
            trs(
              [x - 0.22 + (k % 3) * 0.22, 1.55, -0.12 + Math.floor(k / 3) * 0.24],
              [0, 0, 0],
              0.13,
            ),
          );
        }
      });
      // Flower buckets on the ground.
      for (const [x, color] of [
        [-1.0, '#ff7aa8'],
        [0.9, '#ffc928'],
      ] as const) {
        b.add(T.cylinder, '#8fb8d8', trs([x, 0, 1.0], [0, 0, 0], [0.28, 0.5, 0.28]));
        for (let k = 0; k < 5; k++) {
          const a = (k / 5) * Math.PI * 2;
          b.add(
            T.sphereLow,
            color,
            trs(
              [x + Math.cos(a) * 0.14, 0.62 + (k % 2) * 0.08, 1.0 + Math.sin(a) * 0.14],
              [0, 0, 0],
              0.12,
            ),
          );
        }
      }
    }),
  );
  const signBoard = signMesh(sign, 2.6, 0.65);
  signBoard.position.set(0, 3.35, 0.95);
  signBoard.rotation.x = -0.05;
  group.add(signBoard);
  group.add(pickBox(4, 3.5, 2.6, 'market'));
  return group;
}

function buildBoard(sign: SignTexture): { group: THREE.Group; notes: THREE.Mesh[] } {
  const group = new THREE.Group();
  group.add(
    staticMesh((b) => {
      for (const x of [-0.95, 0.95])
        b.add(T.cylinder, WOOD_DARK, trs([x, 0, 0], [0, 0, 0], [0.09, 2.5, 0.09]));
      b.add(T.box, WOOD, trs([0, 1.65, 0], [0, 0, 0], [2.1, 1.35, 0.12]), { color2: WOOD_LIGHT });
      b.add(T.box, WOOD_DARK, trs([0, 1.65, -0.02], [0, 0, 0], [2.3, 1.55, 0.08]));
      for (const side of [-1, 1]) {
        b.add(T.box, '#7fb069', trs([side * 0.6, 2.62, 0], [0, 0, -side * 0.45], [1.4, 0.1, 0.6]));
      }
    }),
  );
  const notes: THREE.Mesh[] = [];
  const noteGeometry = new THREE.PlaneGeometry(0.5, 0.62);
  const noteColors = ['#fffdf5', '#fff1f6', '#f3fbff'];
  for (let i = 0; i < 3; i++) {
    const note = new THREE.Mesh(noteGeometry, toon(noteColors[i] ?? '#fff'));
    note.position.set(-0.62 + i * 0.62, 1.62, 0.075);
    note.rotation.z = (i - 1) * 0.08;
    const pin = new THREE.Mesh(T.sphere, toon('#ef5d5d'));
    pin.scale.setScalar(0.05);
    pin.position.set(0, 0.24, 0.02);
    note.add(pin);
    notes.push(note);
    group.add(note);
  }
  const title = signMesh(sign, 1.2, 0.32);
  title.position.set(0, 2.45, 0.08);
  group.add(title);
  group.add(pickBox(2.6, 2.8, 1.2, 'board'));
  return { group, notes };
}

function buildFence(): THREE.Group {
  const { minX, maxX, minZ, maxZ, gate } = LAYOUT.fence;
  const posts: Array<[number, number]> = [];
  const rails: Array<{ x: number; z: number; length: number; angle: number }> = [];
  const edge = (ax: number, az: number, bx: number, bz: number) => {
    const length = Math.hypot(bx - ax, bz - az);
    const steps = Math.max(1, Math.round(length / 2));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      posts.push([ax + (bx - ax) * t, az + (bz - az) * t]);
    }
    rails.push({ x: (ax + bx) / 2, z: (az + bz) / 2, length, angle: Math.atan2(bz - az, bx - ax) });
  };
  edge(minX, minZ, maxX, minZ);
  edge(minX, minZ, minX, maxZ);
  edge(maxX, minZ, maxX, maxZ);
  edge(minX, maxZ, -gate, maxZ);
  edge(gate, maxZ, maxX, maxZ);

  const group = new THREE.Group();
  const postGeometry = new THREE.BoxGeometry(0.2, 1.1, 0.2).translate(0, 0.55, 0);
  const postMesh = new THREE.InstancedMesh(postGeometry, toon('#fffaf0'), posts.length);
  const m = new THREE.Matrix4();
  posts.forEach(([x, z], i) => postMesh.setMatrixAt(i, m.makeTranslation(x, 0, z)));
  postMesh.castShadow = true;
  group.add(postMesh);

  const railMesh = new THREE.InstancedMesh(T.box, toon(WOOD_LIGHT), rails.length * 2);
  rails.forEach((r, i) => {
    for (let k = 0; k < 2; k++) {
      railMesh.setMatrixAt(
        i * 2 + k,
        trs([r.x, 0.45 + k * 0.38, r.z], [0, -r.angle, 0], [r.length, 0.1, 0.08]),
      );
    }
  });
  railMesh.castShadow = true;
  group.add(railMesh);

  // Gate posts with little caps.
  for (const x of [-gate, gate]) {
    const cap = new THREE.Mesh(T.sphere, toon('#ff8fab'));
    cap.scale.setScalar(0.18);
    cap.position.set(x, 1.2, maxZ);
    group.add(cap);
  }
  return group;
}

function buildBeehive(): THREE.Group {
  const group = new THREE.Group();
  const box = new RoundedBoxGeometry(1, 0.42, 0.8, 2, 0.08);
  group.add(
    staticMesh((b) => {
      b.add(T.box, WOOD_DARK, trs([0, 0.3, 0], [0, 0, 0], [1.1, 0.6, 0.9]));
      for (let i = 0; i < 3; i++) {
        b.add(
          box,
          i % 2 === 0 ? '#ffd166' : '#ffe9a6',
          trs([0, 0.82 + i * 0.42, 0], [0, 0, 0], [1 - i * 0.05, 1, 1 - i * 0.05]),
        );
      }
      b.add(T.box, '#e9a23b', trs([0, 2.12, 0], [0, 0, 0], [1.15, 0.12, 0.95]));
      b.add(T.box, '#4a3020', trs([0, 0.72, 0.41], [0, 0, 0], [0.3, 0.08, 0.02]));
    }),
  );
  box.dispose();
  return group;
}

function buildCompost(): THREE.Group {
  const group = new THREE.Group();
  group.add(
    staticMesh((b) => {
      for (let i = 0; i < 4; i++) {
        b.add(
          T.box,
          i % 2 ? '#a8744a' : '#b88454',
          trs([0, 0.2 + i * 0.26, 0], [0, 0, 0], [1.4, 0.22, 1.2]),
        );
      }
      b.add(T.box, '#5b3a24', trs([0, 1.08, 0], [0, 0, 0], [1.25, 0.1, 1.05]));
      for (let i = 0; i < 5; i++) {
        b.add(
          T.sphereLow,
          '#7cc35f',
          trs([-0.4 + i * 0.2, 1.18, (i % 2) * 0.2 - 0.1], [0, 0, 0], [0.08, 0.12, 0.08]),
        );
      }
    }),
  );
  return group;
}

function sprinklerPositions(level: number): Array<[number, number]> {
  const gaps = [-2, -1, 0, 1, 2].map((g) => g * PLOT_SPACING);
  const out: Array<[number, number]> = [];
  for (const x of gaps) {
    for (const z of gaps) {
      const ring = Math.max(Math.abs(x), Math.abs(z)) / PLOT_SPACING;
      const inner = Math.abs(x) === PLOT_SPACING && Math.abs(z) === PLOT_SPACING;
      if (
        level >= 3 ||
        (level === 2 && (ring <= 1 || (ring === 2 && (x === 0 || z === 0)))) ||
        (level === 1 && inner)
      ) {
        out.push([x, z]);
      }
    }
  }
  return out;
}

function buildMailbox(sign: SignTexture): THREE.Group {
  const group = new THREE.Group();
  group.add(
    staticMesh((b) => {
      b.add(T.cylinder, WOOD_DARK, trs([0, 0, 0], [0, 0, 0], [0.08, 1.0, 0.08]));
      b.add(T.box, '#7cc6fe', trs([0, 1.15, 0], [0, 0, 0], [0.45, 0.36, 0.7]));
      b.add(T.cylinder, '#7cc6fe', trs([0, 1.33, -0.35], [Math.PI / 2, 0, 0], [0.225, 0.7, 0.18]));
      b.add(T.box, '#ef5d5d', trs([0.26, 1.35, 0.1], [0, 0, 0], [0.04, 0.35, 0.12]));
      // Farm name sign post.
      b.add(T.cylinder, WOOD_DARK, trs([1.6, 0, 0], [0, 0, 0], [0.09, 1.7, 0.09]));
    }),
  );
  const board = signMesh(sign, 2.4, 0.6);
  board.position.set(1.6, 1.55, 0.1);
  group.add(board);
  return group;
}

/** All man-made structures on the homestead plus their small animations. */
export class FarmProps {
  readonly group = new THREE.Group();
  readonly pickables: THREE.Object3D[] = [];
  /** World position smoke rises from. */
  readonly chimneyTop = new THREE.Vector3(LAYOUT.house.x + 2.2, 6.7, LAYOUT.house.z - 1.2);
  readonly beehivePosition = new THREE.Vector3(LAYOUT.beehive.x, 1.2, LAYOUT.beehive.z);

  private readonly blades: THREE.Group;
  private readonly notes: THREE.Mesh[];
  private readonly beehive: THREE.Group;
  private readonly compost: THREE.Group;
  private readonly sprinklers: THREE.InstancedMesh;
  private readonly windowMaterial: THREE.MeshToonMaterial;
  private readonly porchLight: THREE.PointLight;
  private readonly farmSign = new SignTexture('', { width: 512, height: 128 });
  private sprinklerLevel = -1;
  private noteBounce = 0;
  private lastOrderCount = -1;
  readonly sprinklerSpots: THREE.Vector3[] = [];

  constructor() {
    this.windowMaterial = toonUnique({
      color: '#9fc9e8',
      emissive: '#ffc86b',
      emissiveIntensity: 0,
    });

    const house = buildHouse(this.windowMaterial);
    house.position.set(LAYOUT.house.x, 0, LAYOUT.house.z);
    this.group.add(house);

    this.porchLight = new THREE.PointLight('#ffc86b', 0, 14, 1.6);
    this.porchLight.position.set(LAYOUT.house.x + 1.15, 2.5, LAYOUT.house.z + 3.6);
    this.group.add(this.porchLight);

    const windmill = buildWindmill();
    windmill.group.position.set(LAYOUT.windmill.x, 0, LAYOUT.windmill.z);
    windmill.group.rotation.y = 0.55;
    this.blades = windmill.blades;
    this.group.add(windmill.group);

    const market = buildMarket(
      new SignTexture('🌸 Market', { background: '#fff1f6', border: '#ff8fab', color: '#c2456e' }),
    );
    market.position.set(LAYOUT.market.x, 0, LAYOUT.market.z);
    market.rotation.y = -Math.PI / 2;
    this.group.add(market);

    const board = buildBoard(new SignTexture('Orders', { width: 384, height: 104 }));
    board.group.position.set(LAYOUT.board.x, 0, LAYOUT.board.z);
    board.group.rotation.y = -Math.PI / 2;
    this.notes = board.notes;
    this.group.add(board.group);

    this.group.add(buildFence());

    const mailbox = buildMailbox(this.farmSign);
    mailbox.position.set(-4.6, 0, LAYOUT.fence.maxZ - 1.4);
    this.group.add(mailbox);

    this.beehive = buildBeehive();
    this.beehive.position.set(LAYOUT.beehive.x, 0, LAYOUT.beehive.z);
    this.beehive.rotation.y = 0.6;
    this.group.add(this.beehive);

    this.compost = buildCompost();
    this.compost.position.set(LAYOUT.compost.x, 0, LAYOUT.compost.z);
    this.compost.rotation.y = -0.4;
    this.group.add(this.compost);

    const sprinklerGeometry = (() => {
      const b = new GeometryBuilder();
      b.add(T.cylinder, '#9aa5b1', trs([0, 0, 0], [0, 0, 0], [0.09, 0.45, 0.09]));
      b.add(T.sphere, '#5fb4ff', trs([0, 0.5, 0], [0, 0, 0], [0.13, 0.09, 0.13]));
      return b.build().base;
    })();
    this.sprinklers = new THREE.InstancedMesh(sprinklerGeometry, vertexToon, 25);
    this.sprinklers.count = 0;
    this.group.add(this.sprinklers);

    this.group.traverse((o) => {
      if (o.userData.pick) this.pickables.push(o);
    });
  }

  update(dt: number, time: number, state: GameState, night: number): void {
    this.blades.rotation.z -= dt * 0.9;

    this.windowMaterial.emissiveIntensity = night * 1.2;
    this.porchLight.intensity = night * 7;

    this.beehive.visible = state.upgrades.beehive > 0;
    this.compost.visible = state.upgrades.compost > 0;
    this.farmSign.setText(state.farmName);

    if (state.upgrades.sprinkler !== this.sprinklerLevel) {
      this.sprinklerLevel = state.upgrades.sprinkler;
      const spots = sprinklerPositions(this.sprinklerLevel);
      this.sprinklerSpots.length = 0;
      spots.forEach(([x, z], i) => {
        this.sprinklers.setMatrixAt(i, trs([x, 0, z]));
        this.sprinklerSpots.push(new THREE.Vector3(x, 0.55, z));
      });
      this.sprinklers.count = spots.length;
      this.sprinklers.instanceMatrix.needsUpdate = true;
    }

    const orderCount = state.orders.length;
    if (orderCount > this.lastOrderCount && this.lastOrderCount >= 0) this.noteBounce = 1;
    this.lastOrderCount = orderCount;
    this.noteBounce = Math.max(0, this.noteBounce - dt * 1.5);
    this.notes.forEach((note, i) => {
      note.visible = i < orderCount;
      note.rotation.z = (i - 1) * 0.08 + Math.sin(time * 12 + i) * 0.15 * this.noteBounce;
    });
  }
}

/** Where the farmer stands to use each prop (just in front of it). */
export const PROP_STANDS: Readonly<Record<PropId, { x: number; z: number }>> = {
  market: { x: LAYOUT.market.x - 2.2, z: LAYOUT.market.z },
  board: { x: LAYOUT.board.x - 1.5, z: LAYOUT.board.z },
  house: { x: LAYOUT.house.x, z: LAYOUT.house.z + 4.6 },
};
